import { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import sidebarNav, { getNavIcon, INavbarItem } from '../../config/sidebarNav';
import SidebarContext from '../../setup/sidebarContext';
import { Icon } from '@iconify/react';
import classes from './Sidebar.module.scss';
import { useAppActions } from 'actions/app-actions';
import Images from 'assets/images';
import { useSessionActions } from 'actions/session-actions';
import { useAppSelector } from 'setup/store';
import UrlImage from 'components/lib/url-image/UrlImage';
import { useAccessActions } from 'actions/access-actions';

function Sidebar() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set());
  const [openedViaHover, setOpenedViaHover] = useState(false);
  const hoverOpenedRef = useRef(false);
  const { width } = useWindowSize();
  const location = useLocation();
  const sidebarCtx = useContext(SidebarContext);
  const { logoutUser } = useAppActions();
  const { t } = useTranslation();

  const allowedFeatures = useAppSelector((state) => state.sessionReducer.allowedFeatures);
  const selectedBrand = useAppSelector((state) => state.sessionReducer.selectedBrand);

  const [_sidebarItems, setSideBarItems] = useState<INavbarItem[]>();

  const { setSelectedBrand } = useSessionActions();

  function openSidebarHandler() {
    //for width>768(tablet size) if sidebar was open in width<768 was opened too.
    //just in case of tablet size and smaller then, sidebar__open can added.
    if (width <= 768) document.body.classList.toggle('sidebar__open');
  }

  function toggleSubmenu(index: number) {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }

  async function logoutHandler() {
    openSidebarHandler();
    await logoutUser();
  }

  useEffect(() => {
    const curPath = window.location.pathname.split('/')[1];
    const filteredItems = sidebarNav.filter((item) =>
      item.module ? (allowedFeatures as string[]).indexOf(item.module) >= 0 : true,
    );
    const activeItem = filteredItems.findIndex((item) => item.section === curPath);
    setSideBarItems(filteredItems);
    setActiveIndex(curPath.length === 0 ? 0 : activeItem);
  }, [location]);

  // Clear hover flag when sidebar is manually closed or opened
  useEffect(() => {
    if (!sidebarCtx.isOpen) {
      // Sidebar closed - clear hover flag
      setOpenedViaHover(false);
      hoverOpenedRef.current = false;
    } else if (sidebarCtx.isOpen && !hoverOpenedRef.current) {
      // Sidebar opened but not via hover - clear hover flag (manual open)
      setOpenedViaHover(false);
    }
  }, [sidebarCtx.isOpen]);

  const _handleClickOnBrand = () => {
    setSelectedBrand(undefined);
  };

  const handleSidebarMouseEnter = () => {
    // Open sidebar when mouse enters the sidebar panel (only on desktop)
    if (!sidebarCtx.isOpen && width > 768) {
      hoverOpenedRef.current = true;
      setOpenedViaHover(true);
      sidebarCtx.toggleSidebar();
      // Reset ref after a short delay to allow useEffect to process
      setTimeout(() => {
        hoverOpenedRef.current = false;
      }, 100);
    }
  };

  const handleSidebarMouseLeave = () => {
    // Close sidebar if it was opened via hover (only on desktop)
    if (openedViaHover && sidebarCtx.isOpen && width > 768) {
      hoverOpenedRef.current = false;
      setOpenedViaHover(false);
      sidebarCtx.toggleSidebar();
    }
  };

  return (
    <div
      className={`${classes.sidebar} ${!sidebarCtx.isOpen && classes.sidebar_close}`}
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      <div className={classes.sidebar__logo} onClick={_handleClickOnBrand}>
        {selectedBrand ? (
          <UrlImage
            uri={selectedBrand?.logoUrl}
            signed={true}
            classNames={classes.sidebar}
            id="brandImg"
            style={{
              width: !sidebarCtx.isOpen ? '150px' : 'auto',
            }}
          />
        ) : (
          <></>
        )}
      </div>
      <div className={classes.sidebar__menu}>
        {allowedFeatures && _sidebarItems
          ? _sidebarItems.map((nav, index) => {
              // Check if nav has subMenu property (array of sub-menu items)
              if (Array.isArray((nav as any).subMenu) && (nav as any).subMenu.length > 0) {
                const isExpanded = expandedMenus.has(index);
                return (
                  <div key={`nav-parent-${index}`} className={classes.sidebar__menu__parent}>
                    <div
                      className={`${classes.sidebar__menu__item} ${classes.sidebar__menu__item__parent} ${activeIndex === index && classes.active}`}
                      onClick={() => toggleSubmenu(index)}
                    >
                      <div className={classes.sidebar__menu__item__icon}>{getNavIcon(nav.iconKey)}</div>
                      <div className={classes.sidebar__menu__item__txt}>{t('nav.' + nav.section)}</div>
                      <div className={`${classes.sidebar__menu__item__chevron} ${isExpanded ? classes.expanded : ''}`}>
                        <Icon icon="tabler:chevron-right" />
                      </div>
                    </div>
                    <div
                      className={`${classes.sidebar__submenu} ${isExpanded ? classes.sidebar__submenu__expanded : classes.sidebar__submenu__collapsed}`}
                    >
                      {(nav as any).subMenu.map((subNav: any, subIdx: number) => (
                        <Link
                          to={subNav.link}
                          key={`nav-sub-${index}-${subIdx}`}
                          className={`${classes.sidebar__menu__item} ${activeIndex === index && classes.active}}`}
                          onClick={openSidebarHandler}
                        >
                          <div className={classes.sidebar__submenu__item__icon}>
                            {subNav.iconKey ? getNavIcon(subNav.iconKey) : null}
                          </div>
                          <div className={classes.sidebar__submenu__item__txt}>{t('nav.' + subNav.section)}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              } else {
                return (
                  <Link
                    to={nav.link}
                    key={`nav-${index}`}
                    className={`${classes.sidebar__menu__item} ${activeIndex === index && classes.active}`}
                    onClick={openSidebarHandler}
                  >
                    <div className={classes.sidebar__menu__item__icon}>{getNavIcon(nav.iconKey)}</div>
                    <div className={classes.sidebar__menu__item__txt}>{t('nav.' + nav.section)}</div>
                  </Link>
                );
              }
            })
          : null}
      </div>

      <div className={[classes.sidebar__menu, classes.logout].join('')}>
        <Link to="/login" className={classes.sidebar__menu__item} onClick={logoutHandler}>
          <div className={classes.sidebar__menu__item__icon}>
            <Icon icon="tabler:logout" />
          </div>
          <div className={classes.sidebar__menu__item__txt}>{t('logout')}</div>
        </Link>
      </div>
    </div>
  );
}

export default Sidebar;
